/**
 * Certificates Page
 *
 * Students see their earned certificates.
 * Admins see certificates across their account with filters.
 */

import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isAdmin as isAdminRole } from '../utils/roleHelpers';
import { Certificate } from '../apis/api-client';
import { certificatesClient } from '../apis/client-instances';
import { EmptyState, PageHeader } from '../components/common';
import { CertificateIcon, DownloadIcon, EmailIcon } from '../components/icons';
import { Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, TextField, Typography } from '../components/ui';
import { alert, getFileUrl } from '../hooks/function';
import { RootState } from '../types';

export default function CertificatesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const isAdmin = isAdminRole(currentUser?.role);

  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [emailTarget, setEmailTarget] = useState<Certificate | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    const loadCertificates = async () => {
      try {
        const certs = isAdmin ? await certificatesClient.getAccountCertificates() : await certificatesClient.getMyCertificates();
        setCertificates(certs);
      } catch (err) {
        console.error('Failed to load certificates:', err);
        alert(enqueueSnackbar, 'Failed to load certificates', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadCertificates();
  }, [isAdmin, enqueueSnackbar]);

  const handleDownload = async (cert: Certificate) => {
    try {
      // PDF lives alongside the PNG with the same GUID (swap extension).
      const pdfFilename = cert.imageFilename.replace(/\.png$/i, '.pdf');
      const url = await getFileUrl(pdfFilename);
      if (url) {
        window.open(url, '_blank');
      }
    } catch (err) {
      alert(enqueueSnackbar, 'Failed to download certificate', 'error');
    }
  };

  const openEmailDialog = (cert: Certificate) => {
    setEmailTarget(cert);
    setEmailAddress(currentUser?.email || '');
  };

  const closeEmailDialog = () => {
    if (sendingEmail) return;
    setEmailTarget(null);
  };

  const handleSendEmail = async () => {
    if (!emailTarget || !emailAddress.trim()) return;
    setSendingEmail(true);
    try {
      await certificatesClient.emailCertificate(emailTarget.id, { email: emailAddress.trim() });
      alert(enqueueSnackbar, 'Certificate sent', 'success');
      setEmailTarget(null);
    } catch (err: any) {
      alert(enqueueSnackbar, err?.message || 'Failed to send certificate', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader
        icon={<CertificateIcon />}
        title={isAdmin ? 'Certificates' : 'My Certificates'}
        subtitle={isAdmin ? 'View and download certificates earned by students' : 'Your earned course completion certificates'}
      />

      {certificates.length === 0 ? (
        <EmptyState
          icon={<CertificateIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
          title={isAdmin ? 'No certificates have been earned yet' : "You haven't earned any certificates yet"}
          subtitle={
            isAdmin
              ? 'Certificates are generated when students complete courses with an active certificate template'
              : 'Complete a course to earn your certificate'
          }
        />
      ) : (
        <Grid container spacing={3}>
          {certificates.map((cert) => (
            <Grid item xs={12} sm={6} md={4} key={cert.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.2s',
                }}
              >
                {/* Certificate thumbnail */}
                <CertificateThumbnail cert={cert} />

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom noWrap>
                    {cert.courseTitle}
                  </Typography>
                  {isAdmin && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {cert.studentName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Completed: {new Date(cert.completedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>

                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                  <Button variant="outlined" color="primary" size="small" startIcon={<DownloadIcon />} onClick={() => handleDownload(cert)} sx={{ flex: 1 }}>
                    Download
                  </Button>
                  <Button variant="outlined" color="primary" size="small" startIcon={<EmailIcon />} onClick={() => openEmailDialog(cert)} sx={{ flex: 1 }}>
                    Email
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={emailTarget !== null} onClose={closeEmailDialog} fullWidth maxWidth="sm">
        <DialogTitle>Email certificate</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Send the certificate for <strong>{emailTarget?.courseTitle}</strong> as a PDF attachment.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email address"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            disabled={sendingEmail}
          />
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeEmailDialog} disabled={sendingEmail}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSendEmail} disabled={sendingEmail || !emailAddress.trim()}>
            {sendingEmail ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Certificate thumbnail component
 * Shows a small preview of the certificate image
 */
function CertificateThumbnail({ cert }: { cert: Certificate }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadUrl = async () => {
      const url = await getFileUrl(cert.imageFilename);
      setImageUrl(url || null);
    };
    loadUrl();
  }, [cert.imageFilename]);

  return (
    <Box
      sx={{
        height: 180,
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {imageUrl ? (
        <Box component="img" src={imageUrl} alt={`Certificate for ${cert.courseTitle}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <CertificateIcon sx={{ fontSize: 60, color: 'grey.400' }} />
      )}
    </Box>
  );
}
